import React, { useEffect, useState } from 'react';
import splashLogo from '../assets/splash-logo.png';

interface SplashScreenProps {
  onFinish: () => void;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onFinish }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const handleSkip = () => {
    console.log('🚀 스플래시 화면 건너뛰기');
    setFadeOut(true);
    setTimeout(() => {
      setIsVisible(false);
      onFinish();
    }, 300);
  };

  // 컨텐츠를 점진적으로 보여주기
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setShowContent(true);
    }, 200);

    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    // 2초 후에 페이드 아웃 시작
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 2000);

    // 3초 후에 완전히 숨기고 onFinish 호출
    const finishTimer = setTimeout(() => {
      setIsVisible(false);
      onFinish();
    }, 3000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(finishTimer);
    };
  }, [onFinish]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-1000 cursor-pointer ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        background: 'linear-gradient(135deg, #334155 0%, #475569 50%, #64748b 100%)',
      }}
      onClick={handleSkip}
    >
          {/* Skip 버튼 */}
          <div className={`absolute top-4 right-4 sm:top-8 sm:right-8 transition-opacity duration-500 ${
            fadeOut ? 'opacity-0' : 'opacity-100'
          }`}>
            <button 
              className="text-white/80 hover:text-white text-sm sm:text-base font-semibold transition-all duration-200 px-4 py-2 sm:px-6 sm:py-3 rounded-full border-2 border-white/40 hover:border-white/70 bg-white/20 hover:bg-white/30 backdrop-blur-md shadow-lg hover:shadow-xl transform hover:scale-105"
              onClick={handleSkip}
            >
              건너뛰기
            </button>
          </div>
      
      {/* 부드러운 배경 장식 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-pink-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-400/15 to-cyan-400/15 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-rose-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse-slow delay-500"></div>
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-bl from-violet-400/15 to-purple-400/15 rounded-full blur-3xl animate-pulse-slow delay-1500"></div>
      </div>

      {/* 메인 로고 */}
      <div className="relative z-10 flex flex-col items-center">
        {/* 로고 배경 원형 */}
        <div className={`absolute w-80 h-80 bg-white/10 rounded-full blur-2xl transition-all duration-2000 ${
          fadeOut ? 'scale-150 opacity-0' : 'scale-100 opacity-100'
        }`}></div>
        
            <div 
              className={`relative transform transition-all duration-2000 ease-out ${
                fadeOut 
                  ? 'scale-95 opacity-0 translate-y-8' 
                  : showContent 
                    ? 'scale-100 opacity-100 translate-y-0' 
                    : 'scale-95 opacity-0 translate-y-4'
              }`}
            >
              <img
                src={splashLogo}
                alt="e2b - eat bible everyday"
                className="w-64 sm:w-72 h-auto max-w-[80vw] sm:max-w-[85vw] max-h-[60vh] sm:max-h-[70vh] object-contain"
                style={{
                  filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.4))',
                }}
              />
            </div>

            {/* 서브타이틀 */}
            <div className={`mt-6 sm:mt-8 text-center transition-all duration-1000 delay-700 ${
              fadeOut 
                ? 'opacity-0 translate-y-4' 
                : showContent 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-2'
            }`}>
              <p className="text-white/80 text-sm sm:text-base font-medium tracking-wide">
                eat bible everyday
              </p>
            </div>

        {/* 로딩 애니메이션 */}
        <div className={`mt-12 flex items-center space-x-3 transition-all duration-500 delay-1200 ${
          fadeOut 
            ? 'opacity-0 translate-y-4' 
            : showContent 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-2'
        }`}>
          <div className="w-2.5 h-2.5 bg-rose-300 rounded-full animate-bounce shadow-lg shadow-rose-300/50"></div>
          <div className="w-2.5 h-2.5 bg-pink-300 rounded-full animate-bounce delay-150 shadow-lg shadow-pink-300/50"></div>
          <div className="w-2.5 h-2.5 bg-purple-300 rounded-full animate-bounce delay-300 shadow-lg shadow-purple-300/50"></div>
        </div>

            {/* 로딩 텍스트 */}
            <div className={`mt-4 sm:mt-6 text-center transition-all duration-500 delay-1500 ${
              fadeOut 
                ? 'opacity-0 translate-y-2' 
                : showContent 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-1'
            }`}>
              <p className="text-white/70 text-sm sm:text-base font-medium tracking-wider">
                앱을 시작하는 중...
              </p>
              <p className="text-white/50 text-xs sm:text-sm font-light mt-1">
                화면을 터치하면 건너뛸 수 있습니다
              </p>
            </div>
      </div>
    </div>
  );
};

export default SplashScreen;
